import React from "react";
import { useTranslation } from "react-i18next";
import { Absence } from "../../features/absences/models/Absence";
import { ClosedDay } from "../../features/closed-days/models/ClosedDay";
import {
  addDays,
  getMonthGridDays,
  isSameDay,
  startOfWeek,
  toYmd,
  WeekStartDay,
} from "../../features/calendar/utils/calendar-range";

const MAX_NAMES_PER_CELL = 2;

interface AbsencesMonthGridProps {
  anchor: Date;
  absences: Absence[];
  /** First names for the chips; unknown members render nothing. */
  memberNameById: Map<string, string>;
  /** Studio closed days, shaded like on the calendar. */
  closedByDate: Map<string, ClosedDay>;
  weekStart: WeekStartDay;
}

/** Availability overview: a month grid shading closed days and chip-listing who is away. */
const AbsencesMonthGrid: React.FC<AbsencesMonthGridProps> = ({
  anchor,
  absences,
  memberNameById,
  closedByDate,
  weekStart,
}) => {
  const { t, i18n } = useTranslation();
  const days = getMonthGridDays(anchor, weekStart);
  const today = new Date();
  const weekdayFormat = new Intl.DateTimeFormat(i18n.language, { weekday: "short" });
  const weekdayLabels = Array.from({ length: 7 }, (_, i) =>
    weekdayFormat.format(addDays(startOfWeek(today, weekStart), i)),
  );

  const absencesByDate = new Map<string, Absence[]>();
  for (const absence of absences) {
    // Expand the inclusive range into per-day buckets; ranges are capped server-side.
    let cursor = new Date(`${absence.startDate}T00:00:00`);
    const end = new Date(`${absence.endDate}T00:00:00`);
    while (cursor <= end) {
      const key = toYmd(cursor);
      absencesByDate.set(key, [...(absencesByDate.get(key) ?? []), absence]);
      cursor = addDays(cursor, 1);
    }
  }

  return (
    <div className="with-border overflow-hidden rounded-2xl bg-white dark:bg-primary-10">
      <div className="grid grid-cols-7 border-b border-grey-90 dark:border-grey-50">
        {weekdayLabels.map((label) => (
          <div
            key={label}
            className="px-2 py-2 text-center text-xs font-semibold uppercase text-grey-40 dark:text-grey-80"
          >
            {label}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {days.map((day) => {
          const key = toYmd(day);
          const inMonth = day.getMonth() === anchor.getMonth();
          const closed = closedByDate.get(key);
          const dayAbsences = absencesByDate.get(key) ?? [];
          const names = dayAbsences
            .map((absence) => memberNameById.get(absence.memberId))
            .filter((name): name is string => name !== undefined);
          return (
            <div
              key={key}
              className={`min-h-20 border-b border-r border-grey-95 p-1 dark:border-primary-20 ${
                closed ? "bg-grey-95 dark:bg-primary-20" : ""
              } ${inMonth ? "" : "opacity-40"}`}
            >
              <span
                className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs ${
                  isSameDay(day, today)
                    ? "bg-primary-40 font-semibold text-white dark:bg-primary-90 dark:text-primary-10"
                    : "text-grey-30 dark:text-grey-80"
                }`}
              >
                {day.getDate()}
              </span>
              {closed && (
                <p className="truncate text-[10px] text-grey-40 dark:text-grey-80">
                  {t("absences.closedDay", "Closed")}
                </p>
              )}
              {names.slice(0, MAX_NAMES_PER_CELL).map((name, index) => (
                <p
                  key={`${key}-${index}`}
                  className="truncate rounded bg-primary-95 px-1 text-[10px] text-primary-40 dark:bg-primary-20 dark:text-primary-90"
                >
                  {name}
                </p>
              ))}
              {names.length > MAX_NAMES_PER_CELL && (
                <p className="px-1 text-[10px] text-grey-40 dark:text-grey-80">
                  +{names.length - MAX_NAMES_PER_CELL}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AbsencesMonthGrid;
